//frontend/src/components/pendent-load/PendentLoadDetailsPanel.jsx
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import Select from "react-select";

import {
  useTranslation,
} from "react-i18next";

import {
  Boxes,
  CheckCircle2,
  ChevronDown,
  Circle,
  CircleCheck,
  ClipboardList,
  Clock3,
  History,
  Package,
  Pencil,
  Send,
  Trash2,
  Truck,
  Weight,
} from "lucide-react";

const pendentSelectStyles = {
  container: (base) => ({
    ...base,

    width:
      "100%",

    minWidth:
      0,
  }),

  control: (
    base,
    state
  ) => ({
    ...base,

    minHeight:
      36,

    borderRadius:
      7,

    borderColor:
      state.isFocused
        ? "var(--pendent-select-blue)"
        : "var(--pendent-select-border)",

    background:
      state.isDisabled
        ? "var(--pendent-select-disabled-bg)"
        : "var(--pendent-select-bg)",

    color:
      "var(--pendent-select-text)",

    boxShadow:
      state.isFocused
        ? "0 0 0 3px var(--pendent-select-focus)"
        : "none",

    fontSize:
      11,

    cursor:
      state.isDisabled
        ? "not-allowed"
        : "pointer",

    opacity:
      state.isDisabled
        ? 0.62
        : 1,

    transition:
      "border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease",

    ":hover": {
      borderColor:
        state.isFocused
          ? "var(--pendent-select-blue)"
          : "var(--pendent-select-border-hover)",
    },
  }),

  valueContainer: (base) => ({
    ...base,

    minHeight:
      34,

    padding:
      "0 9px",
  }),

  input: (base) => ({
    ...base,

    color:
      "var(--pendent-select-text)",
  }),

  singleValue: (base) => ({
    ...base,

    color:
      "var(--pendent-select-text)",
  }),

  placeholder: (base) => ({
    ...base,

    color:
      "var(--pendent-select-placeholder)",
  }),

  indicatorSeparator: (base) => ({
    ...base,

    backgroundColor:
      "var(--pendent-select-border)",
  }),

  dropdownIndicator: (
    base,
    state
  ) => ({
    ...base,

    padding:
      7,

    color:
      state.isFocused
        ? "var(--pendent-select-blue)"
        : "var(--pendent-select-muted)",

    ":hover": {
      color:
        "var(--pendent-select-blue)",
    },
  }),

  clearIndicator: (base) => ({
    ...base,

    padding:
      7,

    color:
      "var(--pendent-select-muted)",

    ":hover": {
      color:
        "var(--pendent-select-danger)",
    },
  }),

  menuPortal: (base) => ({
    ...base,

    zIndex:
      99999,
  }),

  menu: (base) => ({
    ...base,

    zIndex:
      99999,

    overflow:
      "hidden",

    border:
      "1px solid var(--pendent-select-border)",

    borderRadius:
      8,

    background:
      "var(--pendent-select-menu-bg)",

    boxShadow:
      "var(--pendent-select-shadow)",

    color:
      "var(--pendent-select-text)",

    fontSize:
      11,
  }),

  menuList: (base) => ({
    ...base,

    padding:
      4,

    background:
      "var(--pendent-select-menu-bg)",
  }),

  option: (
    base,
    state
  ) => ({
    ...base,

    borderRadius:
      6,

    background:
      state.isSelected
        ? "var(--pendent-select-selected)"
        : state.isFocused
          ? "var(--pendent-select-hover)"
          : "var(--pendent-select-menu-bg)",

    color:
      state.isSelected
        ? "#ffffff"
        : "var(--pendent-select-text)",

    cursor:
      "pointer",

    ":active": {
      background:
        state.isSelected
          ? "var(--pendent-select-selected-active)"
          : "var(--pendent-select-hover)",
    },
  }),

  noOptionsMessage: (base) => ({
    ...base,

    color:
      "var(--pendent-select-muted)",
  }),
};

function formatCurrency(
  value,
  locale,
  t
) {
  const numberValue =
    Number(
      value
    );

  if (
    !Number.isFinite(
      numberValue
    )
  ) {
    return t(
      "common.undefined"
    );
  }

  return new Intl.NumberFormat(
    locale,
    {
      style:
        "currency",
      currency:
        "EUR",
    }
  ).format(
    numberValue
  );
}

function formatProposalAmount(
  value,
  currency,
  locale,
  t
) {
  const normalizedValue =
    String(
      value ??
        ""
    )
      .trim()
      .replace(
        ",",
        "."
      );

  const numberValue =
    Number(
      normalizedValue
    );

  if (
    !Number.isFinite(
      numberValue
    )
  ) {
    return t(
      "common.undefined"
    );
  }

  try {
    return new Intl.NumberFormat(
      locale,
      {
        style:
          "currency",

        currency:
          currency ||
          "EUR",
      }
    ).format(
      numberValue
    );
  } catch {
    return numberValue.toLocaleString(
      locale,
      {
        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2,
      }
    );
  }
}

function formatNumber(
  value,
  locale,
  t,
  maximumFractionDigits = 3
) {
  const numberValue =
    Number(
      value
    );

  if (
    !Number.isFinite(
      numberValue
    )
  ) {
    return t(
      "common.undefined"
    );
  }

  return numberValue.toLocaleString(
    locale,
    {
      maximumFractionDigits,
    }
  );
}

function getCarrierInitials(
  value
) {
  const name =
    String(
      value ||
        ""
    ).trim();

  if (
    !name
  ) {
    return "TR";
  }

  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(
      (
        part
      ) =>
        part[0]
    )
    .join("")
    .toUpperCase();
}

function getCarrierStatusLabel(
  value,
  t
) {
  const normalized =
    String(
      value ||
        ""
    )
      .trim()
      .toUpperCase();

  const statusKeys = {
    INVITED:
      "detailsPanel.carrierStatus.invited",
    INTERNAL:
      "detailsPanel.carrierStatus.internal",
    ASSIGNED:
      "detailsPanel.carrierStatus.assigned",
    ACTIVE:
      "detailsPanel.carrierStatus.active",
    ACCEPTED:
      "detailsPanel.carrierStatus.accepted",
    REJECTED:
      "detailsPanel.carrierStatus.rejected",
    PENDING:
      "detailsPanel.carrierStatus.pending",
  };

  const key =
    statusKeys[
      normalized
    ];

  return key
    ? t(
        key
      )
    : value;
}

export default function PendentLoadDetailsPanel({
  loading = false,
  isShipmentRequest = false,
  onSave,
  pendentLoadNumber,
  currentPendentLoad,
  merchandise,
  setMerchandise,
  weight,
  setWeight,
  length,
  setLength,
  width,
  setWidth,
  height,
  setHeight,
  trailerType,
  setTrailerType,
  trailerTypeOther,
  setTrailerTypeOther,
  trailerTypeOptions = [],
  price,
  setPrice,
  price4subcontracted,
  setPrice4subcontracted,
  showPrice4subcontracted = false,
  clientType,
  setClientType,
  selectedClient,
  setSelectedClient,
  clientOptions = [],
  transportCarrierId,
  setTransportCarrierId,
  carrierOptions = [],
  reference,
  setReference,
  notes4driver,
  setNotes4driver,
  merchandiseError,
  weightError,
  lengthError,
  widthError,
  heightError,
  priceError,
  price4subcontractedError,

  priceProposals = [],
  latestPriceProposal = null,
  priceNegotiationLoading = false,
  priceNegotiationSubmitting = false,
  priceNegotiationError = "",
  priceProposalAmount = "",
  setPriceProposalAmount,
  priceProposalNote = "",
  setPriceProposalNote,
  canSendPriceProposal = false,
  canAcceptPriceProposal = false,
  canRejectPriceProposal = false,
  canWithdrawPriceProposal = false,
  waitingForShipperPriceResponse = false,
  onSendPriceProposal,
  onAcceptPriceProposal,
  onRejectPriceProposal,
  onWithdrawPriceProposal,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_pendentload"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt";

  const [
    isEditing,
    setIsEditing,
  ] =
    useState(
      false
    );

  const [
    isInvitedCarriersOpen,
    setIsInvitedCarriersOpen,
  ] =
    useState(
      true
    );

  const selectedCarrier =
    useMemo(
      () =>
        carrierOptions.find(
          (
            option
          ) =>
            String(
              option.value
            ) ===
            String(
              transportCarrierId?.value
            )
        ) ||
        transportCarrierId ||
        null,
      [
        carrierOptions,
        transportCarrierId,
      ]
    );

  const invitedCarriers =
    useMemo(
      () => {
        const rawCandidates =
          currentPendentLoad?.invitedCarriers ||
          currentPendentLoad?.carrierInvitations ||
          currentPendentLoad?.requestConversations ||
          currentPendentLoad?.conversations ||
          [];

        const normalized =
          (
            Array.isArray(
              rawCandidates
            )
              ? rawCandidates
              : []
          )
            .map(
              (
                item,
                index
              ) => {
                const company =
                  item?.carrierCompany ||
                  item?.company ||
                  item;

                const name =
                  company?.name ||
                  company?.companyName ||
                  item?.carrierCompanyName ||
                  item?.carrierName ||
                  "";

                if (
                  !name
                ) {
                  return null;
                }

                return {
                  id:
                    item?.id ||
                    company?.id ||
                    `${name}-${index}`,
                  name,
                  initials:
                    getCarrierInitials(
                      name
                    ),
                  status:
                    item?.proposalStatus ||
                    item?.status ||
                    "INVITED",
                };
              }
            )
            .filter(Boolean);

        if (
          normalized.length >
          0
        ) {
          return normalized;
        }

        if (
          selectedCarrier?.label
        ) {
          return [
            {
              id:
                selectedCarrier.value ||
                "selected-carrier",
              name:
                selectedCarrier.label,
              initials:
                getCarrierInitials(
                  selectedCarrier.label
                ),
              status:
                selectedCarrier.isInternal
                  ? "INTERNAL"
                  : "ASSIGNED",
            },
          ];
        }

        return [];
      },
      [
        currentPendentLoad,
        selectedCarrier,
      ]
    );

  const documents =
    useMemo(
      () => {
        const rawDocuments =
          currentPendentLoad?.documents ||
          currentPendentLoad?.attachments ||
          currentPendentLoad?.files ||
          [];

        return (
          Array.isArray(
            rawDocuments
          )
            ? rawDocuments
            : []
        ).map(
          (
            document,
            index
          ) => ({
            id:
              document?.id ||
              `document-${index}`,
            name:
              document?.name ||
              document?.fileName ||
              document?.originalName ||
              t(
                "detailsPanel.documents.defaultName",
                {
                  number:
                    index +
                    1,
                }
              ),
          })
        );
      },
      [
        currentPendentLoad,
        t,
      ]
    );

  const displayReference =
    String(
      reference ||
        currentPendentLoad?.reference ||
        currentPendentLoad?.loadReference ||
        `PL-${String(
          pendentLoadNumber
        ).padStart(
          5,
          "0"
        )}`
    ).trim();

    const clientName =
    selectedClient?.label ||
    currentPendentLoad?.industryClientName ||
    currentPendentLoad?.carrierClientName ||
    currentPendentLoad
      ?.industryClient
      ?.companyName ||
    currentPendentLoad
      ?.carrierClient
      ?.companyName ||
    t(
      "common.undefined"
    );

  const resolvedClientType =
    clientType ||
    (
      currentPendentLoad
        ?.industryClientId ||
      currentPendentLoad
        ?.industryClient
        ?.id ||
      currentPendentLoad
        ?.industryClientName
        ? "industry"
        : currentPendentLoad
            ?.carrierClientId ||
          currentPendentLoad
            ?.carrierClient
            ?.id ||
          currentPendentLoad
            ?.carrierClientName
          ? "carrier"
          : ""
    );

  useEffect(
    () => {
      if (
        isShipmentRequest
      ) {
        return;
      }

      if (
        !clientType &&
        resolvedClientType
      ) {
        setClientType?.(
          resolvedClientType
        );

        return;
      }

      if (
        selectedClient ||
        !resolvedClientType
      ) {
        return;
      }

      const clientId =
        resolvedClientType ===
        "industry"
          ? currentPendentLoad
              ?.industryClientId ??
            currentPendentLoad
              ?.industryClient
              ?.id ??
            null
          : currentPendentLoad
              ?.carrierClientId ??
            currentPendentLoad
              ?.carrierClient
              ?.id ??
            null;

      const resolvedName =
        resolvedClientType ===
        "industry"
          ? currentPendentLoad
              ?.industryClientName ||
            currentPendentLoad
              ?.industryClient
              ?.companyName ||
            ""
          : currentPendentLoad
              ?.carrierClientName ||
            currentPendentLoad
              ?.carrierClient
              ?.companyName ||
            "";

      const matchingOption =
        clientOptions.find(
          (
            option
          ) => {
            if (
              clientId !==
                null &&
              clientId !==
                undefined &&
              String(
                option.value
              ) ===
                String(
                  clientId
                )
            ) {
              return true;
            }

            return (
              resolvedName &&
              String(
                option.label ||
                  ""
              )
                .trim()
                .toLowerCase() ===
                String(
                  resolvedName
                )
                  .trim()
                  .toLowerCase()
            );
          }
        ) ||
        null;

      if (
        matchingOption
      ) {
        setSelectedClient?.(
          matchingOption
        );

        return;
      }

      if (
        clientId !==
          null &&
        clientId !==
          undefined &&
        resolvedName
      ) {
        setSelectedClient?.({
          value:
            clientId,

          label:
            resolvedName,
        });
      }
    },
    [
      clientOptions,
      clientType,
      currentPendentLoad,
      isShipmentRequest,
      resolvedClientType,
      selectedClient,
      setClientType,
      setSelectedClient,
    ]
  );

  const carrierName =
    selectedCarrier?.label ||
    currentPendentLoad?.carrierName ||
    t(
      "detailsPanel.carrierUndefined"
    );

  const dimensions =
    [
      length,
      width,
      height,
    ]
      .map(
        (
          value
        ) =>
          Number.isFinite(
            Number(
              value
            )
          )
            ? Number(
                value
              ).toFixed(
                2
              )
            : null
      )
      .filter(Boolean);

  const clientTypeOptions =
    useMemo(
      () => [
        {
          value:
            "industry",
          label:
            t(
              "fields.industryClient"
            ),
        },
        {
          value:
            "carrier",
          label:
            t(
              "fields.carrierClient"
            ),
        },
      ],
      [
        t,
      ]
    );

  const latestProposalByType =
    String(
      latestPriceProposal
        ?.proposedByType ||
        ""
    )
      .trim()
      .toUpperCase();

  const latestProposalStatus =
    String(
      latestPriceProposal
        ?.status ||
        ""
    )
      .trim()
      .toUpperCase();

  const latestProposalCurrency =
    String(
      latestPriceProposal
        ?.currency ||
        "EUR"
    )
      .trim()
      .toUpperCase();

  const displayedProposalAmount =
    latestPriceProposal
      ?.amount ??
    price;

  const displayedProposalLabel =
    latestPriceProposal
      ? latestProposalByType ===
        "SHIPPER"
        ? t(
            "negotiation.shipperProposal"
          )
        : t(
            "negotiation.yourProposal"
          )
      : t(
          "negotiation.shipperTargetPrice"
        );

  const proposalStatusLabel =
    {
      PENDING:
        t(
          "negotiation.status.pending"
        ),

      ACCEPTED:
        t(
          "negotiation.status.accepted"
        ),

      REJECTED:
        t(
          "negotiation.status.rejected"
        ),

      WITHDRAWN:
        t(
          "negotiation.status.withdrawn"
        ),

      NOT_SELECTED:
        t(
          "negotiation.status.notSelected"
        ),
    }[
      latestProposalStatus
    ] ||
    "";

  const normalizedDisplayedProposalAmount =
    String(
      displayedProposalAmount ??
        ""
    )
      .trim()
      .replace(
        ",",
        "."
      );

  const displayedProposalNumericAmount =
    Number(
      normalizedDisplayedProposalAmount
    );

  const hasNegotiableDisplayedPrice =
    Number.isFinite(
      displayedProposalNumericAmount
    ) &&
    displayedProposalNumericAmount >
      0;

  const terminalProposalStatuses =
    new Set([
      "ACCEPTED",
      "REJECTED",
      "WITHDRAWN",
      "NOT_SELECTED",
      "EXPIRED",
      "CANCELLED",
    ]);

  const proposalIsClosed =
    terminalProposalStatuses.has(
      latestProposalStatus
    );

  const latestProposalLooksFromCarrier =
    latestProposalByType.includes(
      "CARRIER"
    );

  const latestProposalLooksFromShipper =
    latestProposalByType.includes(
      "SHIPPER"
    );

  /*
   * A resposta do backend pode ainda não trazer
   * canAccept/canReject, especialmente quando o preço
   * inicial vem diretamente do pedido do shipper.
   *
   * Nesse caso, mostramos Aceitar/Recusar enquanto:
   * - existe um preço válido;
   * - a negociação não está encerrada;
   * - não estamos a aguardar resposta do shipper;
   * - a proposta não é claramente da transportadora.
   */
  const shouldShowAcceptPriceProposal =
    Boolean(
      canAcceptPriceProposal ||
      (
        hasNegotiableDisplayedPrice &&
        !proposalIsClosed &&
        !waitingForShipperPriceResponse &&
        (
          latestProposalLooksFromShipper ||
          !latestProposalLooksFromCarrier
        )
      )
    );

  const shouldShowRejectPriceProposal =
    Boolean(
      canRejectPriceProposal ||
      (
        hasNegotiableDisplayedPrice &&
        !proposalIsClosed &&
        !waitingForShipperPriceResponse &&
        (
          latestProposalLooksFromShipper ||
          !latestProposalLooksFromCarrier
        )
      )
    );

  const shouldShowPriceDecision =
    shouldShowAcceptPriceProposal ||
    shouldShowRejectPriceProposal;

  const startEditingDetails =
    () => {
      const load =
        currentPendentLoad ||
        {};

      if (
        !String(
          merchandise ??
            ""
        ).trim() &&
        load.merchandise !==
          null &&
        load.merchandise !==
          undefined
      ) {
        setMerchandise?.(
          String(
            load.merchandise
          )
        );
      }

      if (
        (
          weight ===
            "" ||
          weight ===
            null ||
          weight ===
            undefined
        ) &&
        load.weight !==
          null &&
        load.weight !==
          undefined
      ) {
        setWeight?.(
          String(
            load.weight
          )
        );
      }

      if (
        !trailerType &&
        load.trailerType
      ) {
        setTrailerType?.(
          load.trailerType
        );
      }

      if (
        !String(
          trailerTypeOther ??
            ""
        ).trim() &&
        load.trailerTypeOther
      ) {
        setTrailerTypeOther?.(
          String(
            load.trailerTypeOther
          )
        );
      }

      if (
        (
          price ===
            "" ||
          price ===
            null ||
          price ===
            undefined
        ) &&
        load.price !==
          null &&
        load.price !==
          undefined
      ) {
        setPrice?.(
          String(
            load.price
          )
        );
      }

      if (
        !String(
          reference ??
            ""
        ).trim()
      ) {
        const nextReference =
          String(
            load.reference ||
            load.loadReference ||
            displayReference ||
            ""
          ).trim();

        if (
          nextReference
        ) {
          setReference?.(
            nextReference
          );
        }
      }

      if (
        !selectedClient
      ) {
        if (
          load.industryClientId
        ) {
          setClientType?.(
            "industry"
          );

          setSelectedClient?.({
            value:
              load.industryClientId,

            label:
              load.industryClientName ||
              load.industryClient
                ?.companyName ||
              "",
          });
        } else if (
          load.carrierClientId
        ) {
          setClientType?.(
            "carrier"
          );

          setSelectedClient?.({
            value:
              load.carrierClientId,

            label:
              load.carrierClientName ||
              load.carrierClient
                ?.companyName ||
              "",
          });
        }
      }

      if (
        !transportCarrierId
      ) {
        if (
          load.carrierId
        ) {
          setTransportCarrierId?.({
            value:
              String(
                load.carrierId
              ),

            label:
              load.carrierName ||
              load.loadsAsCarrier
                ?.companyName ||
              t(
                "fallbacks.carrier"
              ),

            isInternal:
              false,
          });
        } else if (
          load.carrierName
        ) {
          setTransportCarrierId?.({
            value:
              "__internal__",

            label:
              load.carrierName,

            isInternal:
              true,
          });
        }
      }

      if (
        !String(
          notes4driver ??
            ""
        ).trim() &&
        load.notes4driver
      ) {
        setNotes4driver?.(
          String(
            load.notes4driver
          )
        );
      }

      setIsEditing(
        true
      );
    };

  return (
    <section className="shipment-v2-panel shipment-v2-load-panel">
      <div className="shipment-v2-panel-header">
        <h2>
          {t(
            "detailsPanel.title"
          )}
        </h2>

        {!isShipmentRequest && (
          isEditing ? (
            <div className="edit-request-load-details-actions">
              <button
                type="button"
                className="edit-request-load-details-cancel-btn"
                disabled={
                  loading
                }
                onClick={() =>
                  setIsEditing(
                    false
                  )
                }
              >
                {t(
                  "buttons.cancel"
                )}
              </button>

              <button
                type="button"
                className="edit-request-load-details-save-btn"
                disabled={
                  loading
                }
                onClick={
                  onSave
                }
              >
                <CheckCircle2
                  size={
                    15
                  }
                />

                {loading
                  ? t(
                      "buttons.updating"
                    )
                  : t(
                      "detailsPanel.save"
                    )}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="shipment-v2-plain-action edit-request-load-details-edit-btn"
              onClick={
                startEditingDetails
              }
            >
              <Pencil
                size={
                  13
                }
              />

              {t(
                "routePanel.editDetails"
              )}
            </button>
          )
        )}
      </div>

      {isShipmentRequest && (
        <section
          className="pendent-price-negotiation"
          aria-label={
            t(
              "negotiation.title"
            )
          }
          aria-busy={
            priceNegotiationLoading ||
            priceNegotiationSubmitting
          }
        >
          <header className="pendent-price-negotiation__header">
            <div className="pendent-price-negotiation__heading">
              <strong>
                {t(
                  "negotiation.title"
                )}
              </strong>
            </div>

            <span className="pendent-price-negotiation__history">
              <History
                size={
                  12
                }
              />

              {t(
                "negotiation.history"
              )}

              {priceProposals.length >
                0 && (
                <small>
                  {priceProposals.length}
                </small>
              )}
            </span>
          </header>

          {priceNegotiationLoading &&
          !latestPriceProposal ? (
            <div className="pendent-price-negotiation__loading">
              {t(
                "negotiation.loading"
              )}
            </div>
          ) : (
            <>
              <div className="pendent-price-negotiation__summary">
                <div className="pendent-price-negotiation__current">
                  <div className="pendent-price-negotiation__current-label">
                    <span>
                      {displayedProposalLabel}
                    </span>

                    {proposalStatusLabel && (
                      <small
                        className={[
                          "pendent-price-negotiation__status",
                          latestProposalStatus
                            .toLowerCase(),
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            " "
                          )}
                      >
                        {proposalStatusLabel}
                      </small>
                    )}
                  </div>

                  <div className="pendent-price-negotiation__price">
                    <strong>
                      {formatProposalAmount(
                        displayedProposalAmount,
                        latestProposalCurrency,
                        locale,
                        t
                      )}
                    </strong>

                    <small>
                      {latestProposalCurrency}
                    </small>
                  </div>

                  {latestPriceProposal
                    ?.note && (
                    <p className="pendent-price-negotiation__proposal-note">
                      {
                        latestPriceProposal
                          .note
                      }
                    </p>
                  )}
                </div>

                {shouldShowPriceDecision && (
                  <div className="pendent-price-negotiation__decision">
                    {shouldShowAcceptPriceProposal && (
                      <button
                        type="button"
                        className="pendent-price-negotiation__accept"
                        disabled={
                          priceNegotiationLoading ||
                          priceNegotiationSubmitting
                        }
                        onClick={() =>
                          onAcceptPriceProposal?.()
                        }
                      >
                        {priceNegotiationSubmitting
                          ? t(
                              "negotiation.processing"
                            )
                          : t(
                              "negotiation.accept"
                            )}
                      </button>
                    )}

                    {shouldShowRejectPriceProposal && (
                      <button
                        type="button"
                        className="pendent-price-negotiation__reject"
                        disabled={
                          priceNegotiationLoading ||
                          priceNegotiationSubmitting
                        }
                        onClick={() =>
                          onRejectPriceProposal?.()
                        }
                      >
                        {priceNegotiationSubmitting
                          ? t(
                              "negotiation.processing"
                            )
                          : t(
                              "negotiation.reject"
                            )}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {canSendPriceProposal && (
                <div className="pendent-price-negotiation__counter">
                  <span className="pendent-price-negotiation__counter-title">
                    {latestPriceProposal
                      ? t(
                          "negotiation.yourCounterproposal"
                        )
                      : t(
                          "negotiation.yourProposal"
                        )}
                  </span>

                  <div className="pendent-price-negotiation__fields">
                    <label className="pendent-price-negotiation__amount">
                      <span className="pendent-price-negotiation__sr-only">
                        {t(
                          "negotiation.proposalAmount"
                        )}
                      </span>

                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={
                          priceProposalAmount
                        }
                        placeholder={
                          t(
                            "negotiation.amountPlaceholder"
                          )
                        }
                        disabled={
                          priceNegotiationLoading ||
                          priceNegotiationSubmitting
                        }
                        onChange={(
                          event
                        ) =>
                          setPriceProposalAmount?.(
                            event.target
                              .value
                          )
                        }
                      />

                      <small>
                        {latestProposalCurrency}
                      </small>
                    </label>

                    <label className="pendent-price-negotiation__note">
                      <span className="pendent-price-negotiation__sr-only">
                        {t(
                          "negotiation.optionalNote"
                        )}
                      </span>

                      <input
                        type="text"
                        maxLength={
                          1000
                        }
                        value={
                          priceProposalNote
                        }
                        placeholder={
                          t(
                            "negotiation.optionalNotePlaceholder"
                          )
                        }
                        disabled={
                          priceNegotiationLoading ||
                          priceNegotiationSubmitting
                        }
                        onChange={(
                          event
                        ) =>
                          setPriceProposalNote?.(
                            event.target
                              .value
                          )
                        }
                      />
                    </label>
                  </div>

                  <div className="pendent-price-negotiation__actions">
                    <button
                      type="button"
                      className="pendent-price-negotiation__send"
                      disabled={
                        priceNegotiationLoading ||
                        priceNegotiationSubmitting ||
                        !String(
                          priceProposalAmount ||
                            ""
                        ).trim()
                      }
                      onClick={() =>
                        onSendPriceProposal?.()
                      }
                    >
                      <Send
                        size={
                          14
                        }
                      />

                      {priceNegotiationSubmitting
                        ? t(
                            "negotiation.sending"
                          )
                        : latestPriceProposal
                          ? t(
                              "negotiation.sendCounterproposal"
                            )
                          : t(
                              "negotiation.sendProposal"
                            )}
                    </button>
                  </div>
                </div>
              )}

              {canWithdrawPriceProposal && (
                <div className="pendent-price-negotiation__withdraw-row">
                  <button
                    type="button"
                    className="pendent-price-negotiation__withdraw"
                    disabled={
                      priceNegotiationLoading ||
                      priceNegotiationSubmitting
                    }
                    onClick={() =>
                      onWithdrawPriceProposal?.()
                    }
                  >
                    <Trash2
                      size={
                        13
                      }
                    />

                    {priceNegotiationSubmitting
                      ? t(
                          "negotiation.withdrawing"
                        )
                      : t(
                          "negotiation.withdrawProposal"
                        )}
                  </button>
                </div>
              )}

              {waitingForShipperPriceResponse && (
                <div className="pendent-price-negotiation__waiting">
                  <Clock3
                    size={
                      14
                    }
                  />

                  {t(
                    "negotiation.waitingForShipper"
                  )}
                </div>
              )}

              {!latestPriceProposal &&
                !canSendPriceProposal &&
                !priceNegotiationLoading && (
                  <div className="pendent-price-negotiation__empty">
                    {t(
                      "negotiation.noProposal"
                    )}
                  </div>
                )}
            </>
          )}

          {priceNegotiationError && (
            <div
              className="pendent-price-negotiation__error"
              role="alert"
            >
              {priceNegotiationError}
            </div>
          )}
        </section>
      )}

      {!isShipmentRequest && (
        
      <div className="edit-request-invited-carriers">
        <button
          type="button"
          className="edit-request-invited-carriers__header"
          aria-expanded={
            isInvitedCarriersOpen
          }
          onClick={() =>
            setIsInvitedCarriersOpen(
              (
                currentValue
              ) =>
                !currentValue
            )
          }
        >
          <span className="edit-request-invited-carriers__heading">
            <span>
              {t(
                "detailsPanel.invitedCarriers"
              )}
            </span>

            <strong>
              {invitedCarriers.length}
            </strong>
          </span>

          <ChevronDown
            size={
              15
            }
            className={
              isInvitedCarriersOpen
                ? "is-open"
                : ""
            }
          />
        </button>

        <div
          className={[
            "edit-request-invited-carriers__body-wrapper",
            isInvitedCarriersOpen
              ? "is-open"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden={
            !isInvitedCarriersOpen
          }
        >
          <div className="edit-request-invited-carriers__body-inner">
            <div className="edit-request-invited-carriers__body">
              {invitedCarriers.length >
              0 ? (
                <div className="edit-request-invited-carriers__list">
                  {invitedCarriers.map(
                    (
                      carrier
                    ) => (
                      <div
                        key={
                          carrier.id
                        }
                        className="edit-request-invited-carrier-row"
                      >
                        <span className="edit-request-invited-carrier-row__avatar">
                          {carrier.initials}
                        </span>

                        <span className="edit-request-invited-carrier-row__name">
                          {carrier.name}
                        </span>

                        <span className="edit-request-invited-carrier-row__status is-invited">
                          {getCarrierStatusLabel(
                            carrier.status,
                            t
                          )}
                        </span>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="edit-request-invited-carriers__empty">
                  {t(
                    "detailsPanel.noAssociatedCarriers"
                  )}
                </div>
              )}

              <div className="edit-request-invited-carriers__footer">
                <strong>
                  {t(
                    "detailsPanel.proposalsReceivedCount",
                    {
                      count:
                        currentPendentLoad?.quotationsReceivedCount ??
                        0,
                    }
                  )}
                </strong>

                <span>
                  {t(
                    "detailsPanel.privateChatFuture"
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {isEditing && !isShipmentRequest ? (
        <>
          <div className="shipment-v2-load-top-grid">
            <div className="shipment-v2-mini-table edit-request-load-details-table">
              <div>
                <span>
                  <Package
                    size={
                      14
                    }
                  />

                  {t(
                    "detailsPanel.fields.merchandise"
                  )}
                </span>

                <div className="pendent-load-inline-editor">
                  <input
                    className="pendent-load-inline-input"
                    type="text"
                    value={
                      merchandise
                    }
                    onChange={(event) =>
                      setMerchandise?.(
                        event.target.value
                      )
                    }
                  />

                  {merchandiseError && (
                    <small className="pendent-workspace-editor-error">
                      {merchandiseError}
                    </small>
                  )}
                </div>
              </div>

              <div>
                <span>
                  <Boxes
                    size={
                      14
                    }
                  />

                  {t(
                    "detailsPanel.fields.dimensions"
                  )}
                </span>

                <div className="edit-request-load-dimensions is-editing">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="edit-request-load-dimension-input"
                    value={
                      length ??
                      ""
                    }
                    aria-label="Comprimento"
                    placeholder="C"
                    onChange={(event) =>
                      setLength?.(
                        event.target.value
                      )
                    }
                  />

                  <span
                    className="edit-request-load-dimension-separator"
                    aria-hidden="true"
                  >
                    ×
                  </span>

                  <input
                    type="text"
                    inputMode="decimal"
                    className="edit-request-load-dimension-input"
                    value={
                      width ??
                      ""
                    }
                    aria-label="Largura"
                    placeholder="L"
                    onChange={(event) =>
                      setWidth?.(
                        event.target.value
                      )
                    }
                  />

                  <span
                    className="edit-request-load-dimension-separator"
                    aria-hidden="true"
                  >
                    ×
                  </span>

                  <input
                    type="text"
                    inputMode="decimal"
                    className="edit-request-load-dimension-input"
                    value={
                      height ??
                      ""
                    }
                    aria-label="Altura"
                    placeholder="A"
                    onChange={(event) =>
                      setHeight?.(
                        event.target.value
                      )
                    }
                  />

                  <small className="edit-request-load-dimension-unit">
                    {t(
                      "units.meterShort"
                    )}
                  </small>
                </div>

                {(lengthError ||
                  widthError ||
                  heightError) && (
                  <small className="pendent-workspace-editor-error">
                    {lengthError ||
                      widthError ||
                      heightError}
                  </small>
                )}
              </div>

              <div>
                <span>
                  <Weight
                    size={
                      14
                    }
                  />

                  {t(
                    "detailsPanel.fields.weight"
                  )}
                </span>

                <div className="pendent-load-inline-editor">
                  <input
                    className="pendent-load-inline-input"
                    type="number"
                    min="0"
                    step="0.001"
                    inputMode="decimal"
                    value={
                      weight ??
                      ""
                    }
                    onChange={(event) =>
                      setWeight?.(
                        event.target.value
                      )
                    }
                  />

                  {weightError && (
                    <small className="pendent-workspace-editor-error">
                      {weightError}
                    </small>
                  )}
                </div>
              </div>

              <div>
                <span>
                  <Truck
                    size={
                      14
                    }
                  />

                  {t(
                    "detailsPanel.fields.equipment"
                  )}
                </span>

                <div className="pendent-load-inline-editor">
                  <Select
                    isClearable
                    className="pendent-load-inline-select"
                    classNamePrefix="pendent-load-inline-select"
                    placeholder={
                      t(
                        "trailerType.placeholder"
                      )
                    }
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    options={
                      trailerTypeOptions
                    }
                    value={
                      trailerTypeOptions.find(
                        (
                          option
                        ) =>
                          String(
                            option.value
                          ) ===
                          String(
                            trailerType ||
                              ""
                          )
                      ) ||
                      null
                    }
                    onChange={(option) =>
                      setTrailerType?.(
                        option?.value ||
                          null
                      )
                    }
                    styles={
                      pendentSelectStyles
                    }
                    menuPortalTarget={
                      typeof document !==
                      "undefined"
                        ? document.body
                        : null
                    }
                  />
                </div>
              </div>

              {String(
                trailerType ||
                  ""
              ).toUpperCase() ===
                "OTHER" && (
                <div>
                  <span>
                    {t(
                      "detailsPanel.fields.otherTrailer"
                    )}
                  </span>

                  <input
                    className="pendent-load-inline-input"
                    type="text"
                    value={
                      trailerTypeOther
                    }
                    onChange={(event) =>
                      setTrailerTypeOther?.(
                        event.target.value
                      )
                    }
                  />
                </div>
              )}

              <div className="pendent-load-client-row">
                <span>
                  <ClipboardList
                    size={
                      14
                    }
                  />

                  {t(
                    "detailsPanel.fields.client"
                  )}
                </span>

                <div className="pendent-load-client-editor">
                  <div
                    className="pendent-load-client-type-switch"
                    role="group"
                    aria-label={
                      t(
                        "fields.clientType"
                      )
                    }
                  >
                    {clientTypeOptions.map(
                      (
                        option
                      ) => {
                        const isActive =
                          option.value ===
                          resolvedClientType;

                        return (
                          <button
                            key={
                              option.value
                            }
                            type="button"
                            className={[
                              "pendent-load-client-type-option",

                              isActive
                                ? "is-active"
                                : "",
                            ]
                              .filter(
                                Boolean
                              )
                              .join(
                                " "
                              )}
                            aria-pressed={
                              isActive
                            }
                            onClick={() => {
                              if (
                                option.value ===
                                resolvedClientType
                              ) {
                                return;
                              }

                              setClientType?.(
                                option.value
                              );

                              setSelectedClient?.(
                                null
                              );
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      }
                    )}
                  </div>

                  <Select
                    isClearable
                    isDisabled={
                      !resolvedClientType
                    }
                    className="pendent-load-inline-select pendent-load-client-select"
                    classNamePrefix="pendent-load-inline-select"
                    placeholder={
                      t(
                        "placeholders.selectClient"
                      )
                    }
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    options={
                      clientOptions
                    }
                    value={
                      selectedClient
                    }
                    onChange={
                      setSelectedClient
                    }
                    styles={
                      pendentSelectStyles
                    }
                    menuPortalTarget={
                      typeof document !==
                      "undefined"
                        ? document.body
                        : null
                    }
                    menuPosition="fixed"
                  />
                </div>
              </div>
            </div>

            <div className="shipment-v2-mini-table">
              <div>
                <span>
                  {t(
                    "detailsPanel.fields.carrier"
                  )}
                </span>

                <div className="pendent-load-inline-editor">
                  <Select
                    isClearable
                    className="pendent-load-inline-select"
                    classNamePrefix="pendent-load-inline-select"
                    placeholder={
                      t(
                        "placeholders.selectTransportCarrier"
                      )
                    }
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    options={
                      carrierOptions
                    }
                    value={
                      selectedCarrier
                    }
                    onChange={
                      setTransportCarrierId
                    }
                    styles={
                      pendentSelectStyles
                    }
                    menuPortalTarget={
                      typeof document !==
                      "undefined"
                        ? document.body
                        : null
                    }
                  />
                </div>
              </div>

              <div>
                <span>
                  {t(
                    "detailsPanel.invitedCarriers"
                  )}
                </span>

                <strong>
                  {invitedCarriers.length}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "detailsPanel.proposalsReceived"
                  )}
                </span>

                <strong>
                  {currentPendentLoad?.quotationsReceivedCount ??
                    0}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "detailsPanel.fields.status"
                  )}
                </span>

                <strong>
                  {t(
                    "common.pending"
                  )}
                </strong>
              </div>
            </div>
          </div>

          <div className="shipment-v2-load-bottom-grid">
            <div className="shipment-v2-mini-table compact edit-request-load-details-table">
              <div>
                <span>
                  {t(
                    "detailsPanel.fields.transportTargetPrice"
                  )}
                </span>

                <div className="pendent-load-inline-editor">
                  <div className="pendent-load-inline-money">
                    <small className="pendent-load-inline-currency">
                      €
                    </small>

                    <input
                      className="pendent-load-inline-input"
                      type="number"
                      step="0.01"
                      value={
                        price
                      }
                      onChange={(event) =>
                        setPrice?.(
                          event.target.value
                        )
                      }
                    />
                  </div>

                  {priceError && (
                    <small className="pendent-workspace-editor-error">
                      {priceError}
                    </small>
                  )}
                </div>
              </div>

              <div>
                <span>
                  {t(
                    "fields.reference"
                  )}
                </span>

                <input
                  className="pendent-load-inline-input"
                  type="text"
                  value={
                    reference ||
                    ""
                  }
                  placeholder={
                    t(
                      "detailsPanel.referencePlaceholder"
                    )
                  }
                  onChange={(event) =>
                    setReference?.(
                      event.target.value
                    )
                  }
                />
              </div>

              {showPrice4subcontracted && (
                <div>
                  <span>
                    {t(
                      "fields.price4subcontracted"
                    )}
                  </span>

                  <div className="pendent-load-inline-editor">
                    <input
                      className="pendent-load-inline-input"
                      type="number"
                      step="0.01"
                      value={
                        price4subcontracted
                      }
                      onChange={(event) =>
                        setPrice4subcontracted?.(
                          event.target.value
                        )
                      }
                    />

                    {price4subcontractedError && (
                      <small className="pendent-workspace-editor-error">
                        {price4subcontractedError}
                      </small>
                    )}
                  </div>
                </div>
              )}

              <div>
                <span>
                  {t(
                    "detailsPanel.fields.merchandiseValue"
                  )}
                </span>

                <strong>
                  {formatCurrency(
                    currentPendentLoad?.merchandisePrice,
                    locale,
                    t
                  )}
                </strong>
              </div>
            </div>

            <div className="shipment-v2-documents-box">
              <div className="shipment-v2-documents-header">
                <h3>
                  {t(
                    "detailsPanel.documents.title"
                  )}
                </h3>

                <span>
                  {t(
                    "detailsPanel.documents.uploadedCount",
                    {
                      count:
                        documents.length,
                    }
                  )}
                </span>
              </div>

              <div className="shipment-v2-documents-list">
                {documents.length >
                0 ? (
                  documents.map(
                    (
                      document
                    ) => (
                      <div
                        key={
                          document.id
                        }
                      >
                        <span>
                          {document.name}
                        </span>

                        <strong className="uploaded">
                          {t(
                            "detailsPanel.documents.uploaded"
                          )}

                          <CircleCheck
                            size={
                              14
                            }
                          />
                        </strong>
                      </div>
                    )
                  )
                ) : (
                  <div>
                    <span>
                      {t(
                        "detailsPanel.documents.none"
                      )}
                    </span>

                    <strong className="missing">
                      -

                      <Circle
                        size={
                          14
                        }
                      />
                    </strong>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="shipment-v2-special-box is-editing">
            <div>
              <h3>
                {t(
                  "fields.notesForDriver"
                )}
              </h3>

              <textarea
                className="edit-request-load-notes-input pendent-load-inline-notes"
                value={
                  notes4driver
                }
                placeholder={
                  t(
                    "detailsPanel.notesPlaceholder"
                  )
                }
                onChange={(event) =>
                  setNotes4driver?.(
                    event.target.value
                  )
                }
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            className={[
              "shipment-v2-load-top-grid",

              isShipmentRequest
                ? "is-shipment-request"
                : "",
            ]
              .filter(
                Boolean
              )
              .join(
                " "
              )}
          >
            <div className="shipment-v2-mini-table edit-request-load-details-table">
              <div>
                <span>
                  <Package
                    size={
                      14
                    }
                  />
                  {t(
                    "detailsPanel.fields.merchandise"
                  )}
                </span>

                <strong>
                  {merchandise ||
                    t(
                      "common.undefined"
                    )}
                </strong>
              </div>

              <div>
                <span>
                  <Boxes
                    size={
                      14
                    }
                  />
                  {t(
                    "detailsPanel.fields.dimensions"
                  )}
                </span>

                {dimensions.length ===
                3 ? (
                  <div className="edit-request-load-dimensions is-readonly">
                    <span className="edit-request-load-dimension-value">
                      {dimensions[0]}
                    </span>

                    <span className="edit-request-load-dimension-separator">
                      ×
                    </span>

                    <span className="edit-request-load-dimension-value">
                      {dimensions[1]}
                    </span>

                    <span className="edit-request-load-dimension-separator">
                      ×
                    </span>

                    <span className="edit-request-load-dimension-value">
                      {dimensions[2]}
                    </span>

                    <small className="edit-request-load-dimension-unit">
                      {t(
                        "units.meterShort"
                      )}
                    </small>
                  </div>
                ) : (
                  <strong>
                    {t(
                      "common.undefined"
                    )}
                  </strong>
                )}
              </div>

              <div>
                <span>
                  <Weight
                    size={
                      14
                    }
                  />
                  {t(
                    "detailsPanel.fields.weight"
                  )}
                </span>

                <strong>
                  {String(
                    weight ??
                      ""
                  ).trim() !==
                    "" &&
                  Number.isFinite(
                    Number(
                      weight
                    )
                  )
                    ? t(
                        "detailsPanel.weightValue",
                        {
                          value:
                            formatNumber(
                              weight,
                              locale,
                              t
                            ),
                        }
                      )
                    : t(
                        "common.undefined"
                      )}
                </strong>
              </div>

              <div>
                <span>
                  <Truck
                    size={
                      14
                    }
                  />
                  {t(
                    "detailsPanel.fields.equipment"
                  )}
                </span>

                <strong>
                  {trailerType
                    ? t(
                        `trailerType.options.${String(
                          trailerType
                        ).toUpperCase()}`
                      )
                    : t(
                        "common.undefined"
                      )}
                </strong>
              </div>

              <div>
                <span>
                  <ClipboardList
                    size={
                      14
                    }
                  />
                  {t(
                    "detailsPanel.fields.client"
                  )}
                </span>

                <strong>
                  {clientName}
                </strong>
              </div>
            </div>

          {!isShipmentRequest && (

            <div className="shipment-v2-mini-table">
              <div>
                <span>
                  {t(
                    "detailsPanel.fields.carrier"
                  )}
                </span>

                <strong>
                  {carrierName}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "detailsPanel.invitedCarriers"
                  )}
                </span>

                <strong>
                  {invitedCarriers.length}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "detailsPanel.proposalsReceived"
                  )}
                </span>

                <strong>
                  {currentPendentLoad?.quotationsReceivedCount ?? 0}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "detailsPanel.fields.status"
                  )}
                </span>

                <strong>
                  {t(
                    "common.pending"
                  )}
                </strong>
              </div>
            </div>
            )}
          </div>

          <div className="shipment-v2-load-bottom-grid">
            <div className="shipment-v2-mini-table compact edit-request-load-details-table">
              <div>
                <span>
                  {t(
                    "detailsPanel.fields.transportTargetPrice"
                  )}
                </span>

                <strong>
                  {formatCurrency(
                    price,
                    locale,
                    t
                  )}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "fields.reference"
                  )}
                </span>

                <strong>
                  {displayReference}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "detailsPanel.fields.merchandiseValue"
                  )}
                </span>

                <strong>
                  {formatCurrency(
                    currentPendentLoad?.merchandisePrice,
                    locale,
                    t
                  )}
                </strong>
              </div>
            </div>

            <div className="shipment-v2-documents-box">
              <div className="shipment-v2-documents-header">
                <h3>
                  {t(
                    "detailsPanel.documents.title"
                  )}
                </h3>

                <span>
                  {t(
                    "detailsPanel.documents.uploadedCount",
                    {
                      count:
                        documents.length,
                    }
                  )}
                </span>
              </div>

              <div className="shipment-v2-documents-list">
                {documents.length >
                0 ? (
                  documents.map(
                    (
                      document
                    ) => (
                      <div
                        key={
                          document.id
                        }
                      >
                        <span>
                          {document.name}
                        </span>

                        <strong className="uploaded">
                          {t(
                            "detailsPanel.documents.uploaded"
                          )}
                          <CircleCheck
                            size={
                              14
                            }
                          />
                        </strong>
                      </div>
                    )
                  )
                ) : (
                  <div>
                    <span>
                      {t(
                        "detailsPanel.documents.none"
                      )}
                    </span>

                    <strong className="missing">
                      -
                      <Circle
                        size={
                          14
                        }
                      />
                    </strong>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="shipment-v2-special-box">
            <div>
              <h3>
                {t(
                  "fields.notesForDriver"
                )}
              </h3>

              <p>
                {notes4driver ||
                  t(
                    "detailsPanel.noDriverNotes"
                  )}
              </p>
            </div>

            {!isShipmentRequest && (
              <button
                type="button"
                aria-label={
                  t(
                    "detailsPanel.editAria"
                  )
                }
                onClick={() =>
                  setIsEditing(
                    true
                  )
                }
              >
                <Pencil
                  size={
                    15
                  }
                />
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}